/**
 * Physical-attribute extraction + highlighting for AssetsPanel.
 *
 * The asset spec (USER YAP 2026-06-26) requires EXTREMELY PHYSICAL
 * descriptions — materials, dimensions, colour, finish, scale — and asks
 * us to HIGHLIGHT the salient physical attributes inline (same spirit as
 * the Character panel's explicit-attribute tagging).
 *
 * This module is the single source of truth for "what counts as a
 * physical attribute", reused by the live add-form detail meter, the
 * inline `<mark>` highlighting on cards, and the edit-form preview — so
 * the three surfaces never drift (DRY).
 */

export type PhysicalKind = "material" | "color" | "finish" | "dimension" | "scale";

export interface PhysicalAttribute {
  /** The matched substring, as it appears in the text. */
  text: string;
  kind: PhysicalKind;
  /** Start index in the source string. */
  start: number;
  /** End index (exclusive) in the source string. */
  end: number;
}

export interface AssetViewAngleDef {
  key: AssetViewAngle;
  label: string;
}

export type AssetViewAngle = "front" | "three-quarter" | "left" | "back";

/**
 * The SECONDARY source angles the 360° turntable is generated from
 * (front / three-quarter / left / back), in display order along the strip.
 */
export const ASSET_VIEW_ANGLES: AssetViewAngleDef[] = [
  { key: "front", label: "Front" },
  { key: "three-quarter", label: "Three-quarter" },
  { key: "left", label: "Left" },
  { key: "back", label: "Back" },
];

// Keyword vocabularies. Lower-cased; matched case-insensitively with
// word boundaries. Ordered longest-first within each list so multi-word
// phrases win over their single-word substrings.
const MATERIALS = [
  "stainless steel",
  "carbon fibre",
  "carbon fiber",
  "frosted glass",
  "tempered glass",
  "brushed metal",
  "cast iron",
  "brass",
  "bronze",
  "copper",
  "chrome",
  "steel",
  "iron",
  "aluminium",
  "aluminum",
  "titanium",
  "pewter",
  "gold",
  "silver",
  "leather",
  "suede",
  "denim",
  "cotton",
  "linen",
  "wool",
  "velvet",
  "canvas",
  "nylon",
  "rubber",
  "silicone",
  "plastic",
  "acrylic",
  "fibreglass",
  "fiberglass",
  "glass",
  "ceramic",
  "porcelain",
  "concrete",
  "stone",
  "marble",
  "granite",
  "wood",
  "oak",
  "walnut",
  "mahogany",
  "pine",
  "bamboo",
  "plywood",
  "cardboard",
  "paper",
  "wax",
  "wicker",
];

const FINISHES = [
  "matte",
  "matt",
  "glossy",
  "satin",
  "polished",
  "brushed",
  "anodised",
  "anodized",
  "lacquered",
  "varnished",
  "enamelled",
  "enameled",
  "enamel",
  "powder-coated",
  "galvanised",
  "galvanized",
  "scratched",
  "scuffed",
  "weathered",
  "rusted",
  "rusty",
  "rust",
  "tarnished",
  "patina",
  "faded",
  "worn",
  "distressed",
  "chipped",
  "cracked",
  "dented",
  "smooth",
  "rough",
  "grainy",
  "textured",
  "translucent",
  "transparent",
  "reflective",
  "frosted",
];

const COLORS = [
  "black",
  "white",
  "grey",
  "gray",
  "charcoal",
  "silver",
  "red",
  "crimson",
  "scarlet",
  "maroon",
  "burgundy",
  "orange",
  "amber",
  "ochre",
  "yellow",
  "gold",
  "green",
  "olive",
  "teal",
  "turquoise",
  "blue",
  "navy",
  "cobalt",
  "indigo",
  "purple",
  "violet",
  "magenta",
  "pink",
  "rose",
  "brown",
  "tan",
  "beige",
  "cream",
  "ivory",
  "bronze",
  "copper",
  "rust-orange",
  "pink-and-teal",
];

const SCALE_WORDS = [
  "life-size",
  "life-sized",
  "full-length",
  "full-size",
  "knee-high",
  "waist-high",
  "shoulder-height",
  "hand-held",
  "handheld",
  "pocket-sized",
  "oversized",
  "undersized",
  "compact",
  "miniature",
  "tiny",
  "huge",
  "massive",
  "bulky",
  "slender",
  "chunky",
  "lightweight",
  "heavy-duty",
];

// number + unit (dimensions, weight) — e.g. "12 cm", "1.8m", "200kg",
// "6 feet", "30in", `5"`, `6'` — plus NxN forms like "12 x 8 cm".
const DIMENSION_RE =
  /\b\d+(?:\.\d+)?\s?(?:×|x)\s?\d+(?:\.\d+)?(?:\s?(?:mm|cm|m|in|inch|inches|ft|feet|foot|kg|g|lb|lbs|oz))?\b|\b\d+(?:\.\d+)?\s?(?:mm|cm|m|in|inch|inches|ft|feet|foot|kg|g|lb|lbs|oz)\b|\b\d+(?:\.\d+)?["']/gi;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildKeywordRegex(words: string[]): RegExp {
  const sorted = [...words].sort((a, b) => b.length - a.length).map(escapeRegExp);
  return new RegExp(`\\b(?:${sorted.join("|")})\\b`, "gi");
}

const KEYWORD_MATCHERS: Array<{ kind: PhysicalKind; re: RegExp }> = [
  { kind: "material", re: buildKeywordRegex(MATERIALS) },
  { kind: "finish", re: buildKeywordRegex(FINISHES) },
  { kind: "color", re: buildKeywordRegex(COLORS) },
  { kind: "scale", re: buildKeywordRegex(SCALE_WORDS) },
];

/**
 * Find all physical attributes in `text`, as a sorted, non-overlapping
 * list. When two categories match the same span (e.g. "gold" is both a
 * material and a colour), the earlier category in priority order wins:
 * dimension > material > finish > color > scale.
 */
export function findPhysicalAttributes(text: string): PhysicalAttribute[] {
  const raw: PhysicalAttribute[] = [];

  // Dimensions first (highest priority).
  for (const m of text.matchAll(DIMENSION_RE)) {
    if (m.index === undefined) continue;
    raw.push({
      text: m[0],
      kind: "dimension",
      start: m.index,
      end: m.index + m[0].length,
    });
  }

  for (const matcher of KEYWORD_MATCHERS) {
    for (const m of text.matchAll(matcher.re)) {
      if (m.index === undefined) continue;
      raw.push({
        text: m[0],
        kind: matcher.kind,
        start: m.index,
        end: m.index + m[0].length,
      });
    }
  }

  // Resolve overlaps: sort by start, then by priority (earlier kind wins),
  // then by length (longer wins). Drop any that overlaps a kept span.
  const priority: PhysicalKind[] = [
    "dimension",
    "material",
    "finish",
    "color",
    "scale",
  ];
  raw.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const pa = priority.indexOf(a.kind);
    const pb = priority.indexOf(b.kind);
    if (pa !== pb) return pa - pb;
    return b.end - b.start - (a.end - a.start);
  });

  const kept: PhysicalAttribute[] = [];
  let lastEnd = -1;
  for (const attr of raw) {
    if (attr.start < lastEnd) continue; // overlaps a kept span
    kept.push(attr);
    lastEnd = attr.end;
  }
  return kept;
}

/**
 * Deduplicated attribute list for chip/meter display (one chip per
 * distinct lowercased text), preserving first-seen order.
 */
export function collectPhysicalAttributes(
  text: string,
): Array<{ text: string; kind: PhysicalKind }> {
  const seen = new Set<string>();
  const out: Array<{ text: string; kind: PhysicalKind }> = [];
  for (const attr of findPhysicalAttributes(text)) {
    const key = attr.text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text: attr.text, kind: attr.kind });
  }
  return out;
}

/**
 * Build a DocumentFragment of the description with physical attributes
 * wrapped in `<mark class="studio-asset-mark" data-kind="…">`.
 */
export function buildHighlightedDescription(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const attrs = findPhysicalAttributes(text);
  if (attrs.length === 0) {
    frag.appendChild(document.createTextNode(text));
    return frag;
  }
  let cursor = 0;
  for (const attr of attrs) {
    if (attr.start > cursor) {
      frag.appendChild(document.createTextNode(text.slice(cursor, attr.start)));
    }
    const mark = document.createElement("mark");
    mark.className = "studio-asset-mark";
    mark.dataset.kind = attr.kind;
    mark.textContent = text.slice(attr.start, attr.end);
    frag.appendChild(mark);
    cursor = attr.end;
  }
  if (cursor < text.length) {
    frag.appendChild(document.createTextNode(text.slice(cursor)));
  }
  return frag;
}
